import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState } from 'react';
import { useParams } from 'react-router';
import { EnrollmentHeader } from '@/features/enrollments/components/EnrollmentHeader';
import { ComplianceCalendar } from '@/features/enrollments/components/ComplianceCalendar';
import { BiosensorReadouts } from '@/features/enrollments/components/BiosensorReadouts';
import { EnrollmentActions } from '@/features/enrollments/components/EnrollmentActions';
// Per-enrollment stub data — one entry per enrollment ID
const STUB_BY_ID = {
    '1': {
        id: '1',
        patientName: 'Ashley Carter',
        treatmentType: 'Microneedling',
        enrollmentCode: 'ACE-8P3KX',
        consentState: 'granted',
        studyDay: 8,
        studyStartDate: '28 Mar 2026',
        lastScanDate: '4 Apr 2026 · 08:41',
        complianceDays: [
            { date: 1, state: 'both' },
            { date: 2, state: 'both' },
            { date: 3, state: 'one' },
            { date: 4, state: 'both' },
            { date: 5, state: 'none' },
            { date: 6, state: 'both' },
            { date: 7, state: 'both' },
            { date: 8, state: 'today' },
            ...Array.from({ length: 13 }, (_, i) => ({ date: 9 + i, state: 'future' })),
        ],
        metrics: [
            { zone: 'Forehead', value: '72.4', unit: 'AU', trend: 'up', delta: '+4.2', deltaStatus: 'on-track' },
            { zone: 'Left Cheek', value: '68.1', unit: 'AU', trend: 'flat', delta: '+0.8', deltaStatus: 'watch' },
            { zone: 'Right Cheek', value: '65.9', unit: 'AU', trend: 'down', delta: '-2.3', deltaStatus: 'concern' },
        ],
    },
    '2': {
        id: '2',
        patientName: 'Jennifer Mitchell',
        treatmentType: 'Chemical Peel',
        enrollmentCode: 'ACE-7X4K2',
        consentState: 'pending',
        studyDay: null,
        studyStartDate: '1 Apr 2026',
        lastScanDate: null,
        complianceDays: [],
        metrics: [],
    },
    '3': {
        id: '3',
        patientName: 'Sarah Thompson',
        treatmentType: 'Laser',
        enrollmentCode: 'ACE-2M9TQ',
        consentState: 'granted',
        studyDay: 21,
        studyStartDate: '15 Mar 2026',
        lastScanDate: '4 Apr 2026 · 09:12',
        complianceDays: [
            { date: 1, state: 'both' },
            { date: 2, state: 'both' },
            { date: 3, state: 'both' },
            { date: 4, state: 'one' },
            { date: 5, state: 'both' },
            { date: 6, state: 'both' },
            { date: 7, state: 'none' },
            { date: 8, state: 'both' },
            { date: 9, state: 'both' },
            { date: 10, state: 'both' },
            { date: 11, state: 'one' },
            { date: 12, state: 'both' },
            { date: 13, state: 'both' },
            { date: 14, state: 'both' },
            { date: 15, state: 'both' },
            { date: 16, state: 'one' },
            { date: 17, state: 'both' },
            { date: 18, state: 'both' },
            { date: 19, state: 'none' },
            { date: 20, state: 'both' },
            { date: 21, state: 'both' },
        ],
        metrics: [
            { zone: 'Forehead', value: '81.2', unit: 'AU', trend: 'up', delta: '+12.8', deltaStatus: 'on-track' },
            { zone: 'Left Cheek', value: '78.5', unit: 'AU', trend: 'up', delta: '+10.4', deltaStatus: 'on-track' },
            { zone: 'Right Cheek', value: '76.9', unit: 'AU', trend: 'flat', delta: '+8.9', deltaStatus: 'on-track' },
        ],
    },
    '4': {
        id: '4',
        patientName: 'Emily Davis',
        treatmentType: 'Microneedling',
        enrollmentCode: 'ACE-5R1NW',
        consentState: 'denied',
        studyDay: null,
        studyStartDate: '2 Apr 2026',
        lastScanDate: null,
        complianceDays: [],
        metrics: [],
    },
};
function ConsentPendingNotice({ patientName, enrollmentCode }) {
    const [copied, setCopied] = useState(false);
    function copyCode() {
        navigator.clipboard.writeText(enrollmentCode).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    }
    return (_jsxs("div", { className: "bg-primary-container rounded-[12px] p-5 flex flex-col gap-4", children: [_jsxs("div", { className: "flex gap-4", children: [_jsx("div", { className: "shrink-0 mt-0.5", children: _jsxs("svg", { width: "20", height: "20", viewBox: "0 0 20 20", fill: "none", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round", className: "text-primary", children: [_jsx("circle", { cx: "10", cy: "10", r: "8" }), _jsx("path", { d: "M10 6v4M10 13.5v.5" })] }) }), _jsxs("div", { className: "flex flex-col gap-1", children: [_jsx("p", { className: "text-[15px] font-medium text-on-surface", children: "Awaiting patient consent" }), _jsxs("p", { className: "text-[13px] font-normal text-on-surface-variant", children: ["Share the enrollment code with ", _jsx("strong", { className: "font-medium text-on-surface", children: patientName }), " via WhatsApp, LINE, or SMS. Biosensor data will appear here once they consent in the Acie app."] })] })] }), _jsxs("div", { className: "bg-surface rounded-[8px] border border-outline-variant px-5 py-4 flex items-center justify-between gap-4", children: [_jsxs("div", { className: "flex flex-col gap-0.5", children: [_jsx("span", { className: "text-[11px] font-medium text-on-surface-variant uppercase tracking-[0.06em]", children: "Enrollment code" }), _jsx("span", { className: "text-[26px] font-bold text-on-surface tabular-nums tracking-[0.12em]", children: enrollmentCode })] }), _jsx("button", { onClick: copyCode, className: "flex items-center gap-2 h-9 px-4 rounded-[8px] border border-outline-variant bg-surface-container-low text-[13px] font-medium text-on-surface hover:bg-surface-container transition-colors shrink-0", children: copied ? (_jsxs(_Fragment, { children: [_jsx("svg", { width: "13", height: "13", viewBox: "0 0 13 13", fill: "none", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round", children: _jsx("path", { d: "M2 6.5l3.5 3.5 5.5-6" }) }), "Copied"] })) : (_jsxs(_Fragment, { children: [_jsxs("svg", { width: "13", height: "13", viewBox: "0 0 13 13", fill: "none", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round", children: [_jsx("rect", { x: "4", y: "4", width: "6", height: "6", rx: "1.5" }), _jsx("path", { d: "M2.5 8.5V3a1 1 0 011-1H9" })] }), "Copy code"] })) })] })] }));
}
function ConsentDeniedNotice({ patientName }) {
    return (_jsxs("div", { className: "bg-surface-container-low border border-outline-variant rounded-[12px] p-5 flex gap-4", children: [_jsx("div", { className: "shrink-0 mt-0.5", children: _jsxs("svg", { width: "20", height: "20", viewBox: "0 0 20 20", fill: "none", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round", className: "text-error", children: [_jsx("circle", { cx: "10", cy: "10", r: "8" }), _jsx("path", { d: "M7 7l6 6M13 7l-6 6" })] }) }), _jsxs("div", { className: "flex flex-col gap-1", children: [_jsx("p", { className: "text-[15px] font-medium text-on-surface", children: "Data access declined" }), _jsxs("p", { className: "text-[13px] font-normal text-on-surface-variant", children: [_jsx("strong", { className: "font-medium text-on-surface", children: patientName }), " declined to share biosensor data for this enrollment. No scan data has been collected."] })] })] }));
}
export function EnrollmentDetailPage() {
    const { id } = useParams();
    const found = id !== undefined ? STUB_BY_ID[id] : undefined;
    const enrollment = found ?? STUB_BY_ID['1'];
    const hasScans = enrollment.studyDay !== null;
    const consentGranted = enrollment.consentState === 'granted';
    const consentPending = enrollment.consentState === 'pending';
    const consentDenied = enrollment.consentState === 'denied' || enrollment.consentState === 'revoked';
    const studyComplete = enrollment.studyDay === 21;
    return (_jsxs("div", { className: "flex flex-col gap-6", children: [_jsx(EnrollmentHeader, { patientName: enrollment.patientName, treatmentType: enrollment.treatmentType, consentState: enrollment.consentState, studyDay: enrollment.studyDay }), _jsx("div", { className: "h-px bg-outline-variant" }), consentPending && (_jsx(ConsentPendingNotice, { patientName: enrollment.patientName, enrollmentCode: enrollment.enrollmentCode })), consentDenied && (_jsx(ConsentDeniedNotice, { patientName: enrollment.patientName })), hasScans && enrollment.studyStartDate && (_jsx(ComplianceCalendar, { days: enrollment.complianceDays, studyStartDate: enrollment.studyStartDate })), consentGranted && hasScans && enrollment.lastScanDate && (_jsx(BiosensorReadouts, { metrics: enrollment.metrics, lastUpdated: enrollment.lastScanDate })), _jsx(EnrollmentActions, { patientName: enrollment.patientName, enrollmentId: enrollment.id, showSendReminder: false, showRevokeAccess: consentGranted, showExportData: studyComplete })] }));
}
